import type { AnalyticsConfig, AnyContext, AnyProperties, ConsentDecisions, ConsentState, PublicConsentPolicy } from "./types.js";
import type { IngestEvent } from "mindbricks-analytics-shared";
import { AUTO_EVENTS, SERVER_ONLY_EVENTS } from "mindbricks-analytics-shared";

import { LocalStore } from "./storage.js";
import { uuid } from "./eventId.js";
import { SessionManager } from "./session.js";
import { IdentityManager } from "./identity.js";
import { Attribution } from "./attribution.js";
import { Transport } from "./transport.js";
import { ConsentManager, type ConsentSource } from "./consent.js";
import { ConsentBanner } from "./banner.js";
import { buildContext } from "./context.js";
import { attachLifecycle } from "./lifecycle.js";
import { attachAutoTracking } from "./auto-track.js";
import { attachHistoryTracking } from "./history.js";
import { CaptureResolver } from "./capture.js";

const DEFAULTS = {
  sessionTimeoutMs: 30 * 60 * 1000,
  batchSize: 25,
  flushIntervalMs: 5_000,
  persistQueue: true,
  sampleRate: 1,
  storagePrefix: "analytics",
} as const;

/**
 * Public API surface (matches the spec):
 *
 *   analytics.init()
 *   analytics.track(eventName, properties?)
 *   analytics.pageView(properties?)
 *   analytics.identify(userId, traits?)
 *   analytics.reset()
 *   analytics.flush()
 *   analytics.setConsent({ analytics, marketing })
 */
export class Analytics {
  private config: Required<Pick<AnalyticsConfig, keyof typeof DEFAULTS>> & AnalyticsConfig;
  private storage: LocalStore;
  private session!: SessionManager;
  private identity!: IdentityManager;
  private attribution!: Attribution;
  private consent!: ConsentManager;
  private banner: ConsentBanner | null = null;
  private transport!: Transport;
  private capture!: CaptureResolver;
  private necessaryEvents: Set<string>;

  private detachLifecycle: () => void = () => {};
  private detachAutoTracking: () => void = () => {};
  private detachHistory: () => void = () => {};

  private initialized = false;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private currentPath: string | null = null;
  private currentPathEnteredAt = 0;

  constructor(config: AnalyticsConfig) {
    if (!config.projectId) throw new Error("[analytics] projectId is required");
    if (!config.apiUrl) throw new Error("[analytics] apiUrl is required");

    this.config = {
      ...DEFAULTS,
      ...config,
    };
    this.storage = new LocalStore(`${this.config.storagePrefix}.${this.config.projectId}`);
    // Default to allowing $page_view + $page_enter + $page_leave + $session_*
    // pre-decision — these are usually classified as "strictly necessary"
    // for the site to function (visitor count is needed for capacity
    // planning / security, not analytics in the GDPR sense). The customer
    // can override or empty this list via config.consent.necessaryEvents.
    const defaultNecessary = [
      AUTO_EVENTS.PAGE_VIEW,
      AUTO_EVENTS.PAGE_ENTER,
      AUTO_EVENTS.PAGE_LEAVE,
      AUTO_EVENTS.SESSION_START,
      AUTO_EVENTS.SESSION_END,
    ];
    this.necessaryEvents = new Set(this.config.consent?.necessaryEvents ?? defaultNecessary);
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.session = new SessionManager(this.storage, this.config.sessionTimeoutMs);
    this.identity = new IdentityManager(this.storage);
    this.attribution = new Attribution(this.storage);
    this.consent = new ConsentManager(this.storage);
    this.consent.setServerSync((decisions, source) =>
      this.postConsentDecision(decisions, source),
    );
    this.transport = new Transport({
      apiUrl: this.config.apiUrl,
      projectId: this.config.projectId,
      batchSize: this.config.batchSize,
      flushIntervalMs: this.config.flushIntervalMs,
      persistQueue: this.config.persistQueue,
      storage: this.storage,
      ...(this.config.debug !== undefined ? { debug: this.config.debug } : {}),
    });
    this.capture = new CaptureResolver({
      storage: this.storage,
      apiUrl: this.config.apiUrl,
      projectId: this.config.projectId,
      debug: this.config.debug,
      // Dashboard-configured `userId` rule routes through identifyInternal so
      // duplicate calls are deduped and the $identify event fires only on change.
      onIdentify: (userId, traits) => this.identifyInternal(userId, traits),
    });
    this.capture.init();
    this.capture.setCategoryGate((key) => this.consent.allowsCategory(key));

    // Fire-and-forget policy fetch. Capture rule gating + banner render
    // both depend on this, but we don't await it — the SDK is usable
    // pre-policy in "necessary-only" mode (any event not in
    // `necessaryEvents` is dropped until a decision lands).
    void this.fetchConsentPolicy();

    // Periodic flush as a safety net (the transport also schedules its own).
    this.flushTimer = setInterval(() => void this.transport.flush(), this.config.flushIntervalMs);

    // session_start fires on every fresh session, including the first.
    if (this.session.getSessionStartedAt() >= Date.now() - 1_000) {
      this.trackInternal(AUTO_EVENTS.SESSION_START, {});
    }

    // First page view + start page timing.
    this.session.startPageTiming();
    this.pageView();

    if (typeof window !== "undefined" && !this.config.disableAutoTracking) {
      this.detachLifecycle = attachLifecycle(this);
      this.detachAutoTracking = attachAutoTracking(this, {
        clicks: this.config.autoTracking?.clicks ?? true,
        forms: this.config.autoTracking?.forms ?? true,
        scroll: this.config.autoTracking?.scroll ?? true,
        rageClick: this.config.autoTracking?.rageClick ?? true,
        deadClick: this.config.autoTracking?.deadClick ?? true,
        errors: this.config.autoTracking?.errors ?? true,
        performance: this.config.autoTracking?.performance ?? true,
      });
      // Zero-config SPA navigation: hook the History API so $page_view fires
      // on pushState/replaceState/popstate without any router integration.
      // pageView() is idempotent for same-path calls, so RouterTracker (or
      // a manual analytics.pageView() call) won't double-emit.
      if (this.config.autoTracking?.history ?? true) {
        this.detachHistory = attachHistoryTracking(this);
      }
    }
  }

  /* ------------------------------- public API ----------------------------- */

  track(eventName: string, properties?: AnyProperties): void {
    if (!this.initialized) return;
    if (!this.eventAllowed(eventName)) return;
    if (this.shouldSample() === false) return;

    if (SERVER_ONLY_EVENTS.has(eventName)) {
      // Help the integrator catch the bug locally; backend would reject anyway.
      if (this.config.debug) {
        console.warn(`[analytics] event "${eventName}" is server-only and was dropped on the client`);
      }
      return;
    }

    const { rotated, previousSessionId } = this.session.touch();
    if (rotated && previousSessionId) {
      // session_end carries the OLD sessionId so dashboards can close out the
      // outgoing session row. session_start uses the new (current) sessionId.
      this.transport.enqueue(this.buildEvent(AUTO_EVENTS.SESSION_END, {}, previousSessionId));
      this.transport.enqueue(this.buildEvent(AUTO_EVENTS.SESSION_START, {}));
    }
    this.transport.enqueue(this.buildEvent(eventName, properties));
  }

  pageView(properties?: AnyProperties): void {
    if (!this.initialized) return;
    const newPath = typeof location !== "undefined" ? location.pathname + location.search : "";

    // Idempotent: same path → no event. This makes the auto-history hook,
    // RouterTracker, and manual pageView() calls coexist safely.
    if (this.currentPath === newPath) return;

    let timeOnPreviousPage = 0;
    let previousPath: string | null = null;
    if (this.currentPath != null && this.currentPath !== newPath) {
      timeOnPreviousPage = Date.now() - this.currentPathEnteredAt;
      previousPath = this.currentPath;
      // Emit a page_leave for the previous path with timing.
      const timing = this.session.pageTiming();
      this.transport.enqueue(
        this.buildEvent(AUTO_EVENTS.PAGE_LEAVE, {
          path: previousPath,
          ...timing,
        }),
      );
    }

    this.currentPath = newPath;
    this.currentPathEnteredAt = Date.now();
    this.session.markPageView();
    this.session.startPageTiming();

    const props: AnyProperties = {
      ...(properties ?? {}),
      previousPath,
      currentPath: newPath,
      fullUrl: typeof location !== "undefined" ? location.href : undefined,
      queryParams: this.collectQueryParams(),
      timeOnPreviousPageMs: timeOnPreviousPage || undefined,
    };

    this.track(AUTO_EVENTS.PAGE_VIEW, props);
    this.transport.enqueue(this.buildEvent(AUTO_EVENTS.PAGE_ENTER, { path: newPath }));
  }

  identify(userId: string, traits?: AnyProperties): void {
    if (!this.initialized) return;
    this.identity.identify(userId, traits);
    // Emit a special identify event so the worker can persist user_profiles.
    this.transport.enqueue(this.buildEvent(AUTO_EVENTS.IDENTIFY, traits ?? {}));
  }

  reset(): void {
    if (!this.initialized) return;
    void this.flush();
    this.identity.reset();
    this.session.reset();
    this.session.startPageTiming();
    this.currentPath = null;
  }

  async flush(): Promise<void> {
    if (!this.initialized) return;
    await this.transport.flush();
  }

  /**
   * Programmatic API for the customer to set consent from their own UI.
   * Legacy v0.3 callers passed `{ analytics, marketing }` — we map that to
   * the new category-based shape for back-compat. v2 callers pass a
   * category map directly via `setConsentDecisions`.
   */
  setConsent(consent: Partial<ConsentState> | ConsentDecisions): void {
    if (!this.initialized) return;
    const decisions: ConsentDecisions = {};
    if ("analytics" in consent && typeof consent.analytics === "boolean") {
      decisions.analytics = consent.analytics;
    }
    if ("marketing" in consent && typeof consent.marketing === "boolean") {
      decisions.marketing = consent.marketing;
    }
    // Pass through any other keys (custom categories).
    for (const [k, v] of Object.entries(consent)) {
      if (typeof v === "boolean" && k !== "analytics" && k !== "marketing") {
        decisions[k] = v;
      }
    }
    this.consent.record(decisions, "api");
  }

  getConsent(): ConsentDecisions {
    return this.consent.get();
  }

  openConsentPreferences(): void {
    if (this.banner) this.banner.open("preferences");
  }

  withdrawConsent(): void {
    if (!this.initialized) return;
    this.consent.withdraw();
    if (this.banner) this.banner.open("banner");
  }

  getConsentPolicy(): PublicConsentPolicy | null {
    return this.consent.getPolicy();
  }

  /* ----------------------------- internal helpers ------------------------- */

  /** Used by lifecycle/auto-track for events that bypass sampling. */
  trackInternal(eventName: string, properties: AnyProperties): void {
    if (!this.initialized) return;
    if (!this.eventAllowed(eventName)) return;
    this.transport.enqueue(this.buildEvent(eventName, properties));
  }

  recordVisibility(visible: boolean): void {
    this.session.recordVisibility(visible);
  }

  flushBeacon(): void {
    if (!this.initialized) return;
    this.transport.flushBeacon();
  }

  queueSize(): number {
    return this.transport.size();
  }

  private buildEvent(eventName: string, properties?: AnyProperties, sessionIdOverride?: string): IngestEvent {
    const ctx: AnyContext = {
      ...buildContext(this.config.environment),
      ...this.attribution.decorateContext(),
    };
    // Dashboard-configured super properties — resolved fresh per event so
    // window globals / DOM / cookies stay current. Caller-passed properties
    // win on collision (explicit beats automatic).
    const superProps = this.capture.resolveSuper();
    const merged: AnyProperties = { ...superProps, ...(properties ?? {}) };
    const userId = this.identity.getUserId();
    const event: IngestEvent = {
      eventId: uuid(),
      eventName,
      timestamp: new Date().toISOString(),
      anonymousId: this.session.getAnonymousId(),
      sessionId: sessionIdOverride ?? this.session.getSessionId(),
      ...(Object.keys(merged).length > 0 ? { properties: merged } : {}),
      context: ctx,
    };
    if (userId) event.userId = userId;
    return event;
  }

  /**
   * Internal identify — used by the capture resolver. Dedupes if the userId
   * hasn't actually changed so we don't emit duplicate $identify events on
   * every page load.
   */
  private identifyInternal(userId: string, traits?: AnyProperties): void {
    if (!this.initialized) return;
    if (this.identity.getUserId() === userId) return;
    this.identity.identify(userId, traits);
    this.transport.enqueue(this.buildEvent(AUTO_EVENTS.IDENTIFY, traits ?? {}));
  }

  private shouldSample(): boolean {
    if (this.config.sampleRate >= 1) return true;
    return Math.random() < this.config.sampleRate;
  }

  /**
   * Gates event emission by consent. Three states:
   *   * No policy applied yet (initial boot): allow everything classified as
   *     "necessary" by config; drop the rest.
   *   * Policy applied + visitor decided: allow events whose category is on,
   *     plus always-necessary events.
   *   * Policy applied + decision pending: allow only necessary events.
   *
   * "Necessary" here is keyed by event name, not capture-rule category —
   * because event names ($click, $page_view) live in the SDK and are not
   * dashboard-configurable. Capture rule categories are gated separately
   * inside CaptureResolver via the gate set in init().
   */
  private eventAllowed(eventName: string): boolean {
    if (this.necessaryEvents.has(eventName)) return true;
    const policy = this.consent.getPolicy();
    if (!policy) return false; // strict default until policy lands
    if (this.consent.needsDecision()) return false;
    return this.consent.allowsCategory("analytics");
  }

  private async fetchConsentPolicy(): Promise<void> {
    try {
      const url =
        `${this.config.apiUrl.replace(/\/$/, "")}/v1/consent/policy` +
        `?projectId=${encodeURIComponent(this.config.projectId)}`;
      const res = await fetch(url, { method: "GET", credentials: "omit" });
      if (!res.ok) return;
      const policy = (await res.json()) as PublicConsentPolicy;
      this.consent.applyPolicy(policy);
      // Bridge to Google Consent Mode v2 if the host page has gtag.
      if (this.config.consent?.bridgeGoogleConsentMode) {
        this.bridgeGoogleConsent();
      }
      // Render banner if the visitor needs to decide.
      if (this.config.consent?.showBanner !== false && typeof document !== "undefined") {
        this.banner = new ConsentBanner(this.consent, this.config.consent?.bannerMount ?? null);
        this.consent.onChange(() => {
          if (this.config.consent?.bridgeGoogleConsentMode) this.bridgeGoogleConsent();
        });
        this.banner.showIfNeeded();
      }
    } catch (e) {
      if (this.config.debug) console.warn("[analytics] consent policy fetch failed", e);
    }
  }

  private async postConsentDecision(decisions: ConsentDecisions, source: ConsentSource): Promise<void> {
    const policy = this.consent.getPolicy();
    if (!policy) return;
    const url = `${this.config.apiUrl.replace(/\/$/, "")}/v1/consent/decision`;
    const body = JSON.stringify({
      projectId: this.config.projectId,
      decision: {
        anonymousId: this.session.getAnonymousId(),
        policyVersion: policy.version,
        policyHash: policy.contentHash,
        decisions,
        source,
      },
    });
    // text/plain so the browser doesn't preflight (mirrors ingest path).
    await fetch(url, {
      method: "POST",
      credentials: "omit",
      headers: { "content-type": "text/plain" },
      body,
      keepalive: true,
    });
  }

  /**
   * Sets Google Consent Mode v2 flags from the visitor's decisions, so Google
   * tags loaded on the same page can react. Customer must have `window.gtag`
   * defined OR pre-load `dataLayer.push(['consent', ...])`. We map our
   * categories to Google's signals: marketing → ad_storage + ad_user_data +
   * ad_personalization; analytics → analytics_storage; functional → functionality_storage.
   */
  private bridgeGoogleConsent(): void {
    if (typeof window === "undefined") return;
    const decisions = this.consent.get();
    const marketing = decisions.marketing === true;
    const analytics = decisions.analytics === true;
    const functional = decisions.functional === true;
    const v = (granted: boolean) => (granted ? "granted" : "denied");
    const payload = {
      ad_storage: v(marketing),
      ad_user_data: v(marketing),
      ad_personalization: v(marketing),
      analytics_storage: v(analytics),
      functionality_storage: v(functional),
      personalization_storage: v(functional),
      security_storage: "granted" as const,
    };
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: unknown[];
    };
    if (typeof w.gtag === "function") {
      w.gtag("consent", "update", payload);
      return;
    }
    if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
    w.dataLayer.push(["consent", "update", payload]);
  }

  private collectQueryParams(): Record<string, string> | undefined {
    if (typeof location === "undefined" || !location.search) return undefined;
    const out: Record<string, string> = {};
    const params = new URLSearchParams(location.search);
    for (const [k, v] of params) out[k] = v;
    return Object.keys(out).length ? out : undefined;
  }

  /* ------------------------ teardown for HMR/tests ----------------------- */

  destroy(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.detachLifecycle();
    this.detachAutoTracking();
    this.detachHistory();
    this.flushTimer = null;
    this.initialized = false;
  }
}
